import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import useAccountList from '../../hooks/queries/useAccountList'
import MainContentDiv from '../../components/templates/MainContent'
import SearchContainer from '../../components/organisms/SearchContainer'
import Search from '../Search'
import ContentsTitle from '../../components/molecules/ContentsTitle'
import commaSeparator from '../../utils/number'
import ErrorMessageFormat from '../../components/molecules/ErrorMessageFormat'
import PaginationSingle from '../../components/molecules/PaginationSingle'
import ContentsWrapper from '../../components/organisms/ContentsWrapper'
import Table from '../../components/molecules/Table'
import { roundDownFixedPoint, weiToEther } from '../../utils/dataFormat'
import Icon from '../../components/atoms/Icon'
import Button from '../../components/atoms/Button'
import PageLoading from '../../components/organisms/PageLoading'

import { TableTit, TranNumSpan } from './styles'
import usePagination from '../../hooks/usePagination'
import { PAGE_SIZE } from '../../constants'

const ACCOUNT_LIMIT = 10_000

function TopAccount() {
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const {
    accounts = [],
    totalAccount = 0,
    totalPage: originalTotalPage = 0,
    isLoading,
  } = useAccountList(currentPage, PAGE_SIZE)
  const totalPage =
    ACCOUNT_LIMIT / PAGE_SIZE < originalTotalPage ? Math.floor(ACCOUNT_LIMIT / PAGE_SIZE) : originalTotalPage

  const { onNext, onPrev, onFirst, onLast } = usePagination({
    currentPage,
    setCurrentPage,
    totalPage,
  })
  const onClickAddress = (value: string) => {
    navigate(`/account/${value}`)
  }

  return (
    <MainContentDiv>
      <SearchContainer>
        <Search />
      </SearchContainer>
      <ContentsTitle
        title="Top Accounts"
        subTitle={
          <TableTit>
            {typeof totalAccount === 'number' ? (
              <p>
                A total of <TranNumSpan>{commaSeparator(totalAccount)}</TranNumSpan> accounts
              </p>
            ) : (
              <ErrorMessageFormat>
                <FormattedMessage id="error.failTotalNum" /> ({totalAccount})
              </ErrorMessageFormat>
            )}
          </TableTit>
        }
      />
      <PaginationSingle
        totalPage={totalPage}
        current={currentPage}
        onClickLeft={onPrev}
        onClickRight={onNext}
        onClickFirstPage={onFirst}
        onClickLastPage={onLast}
      />
      <ContentsWrapper>
        <Table
          columns={[
            { title: 'Rank', dataIndex: 'rank', style: { maxWidth: '10px', minWidth: '0px' } },
            { title: 'Address', dataIndex: 'address', style: { maxWidth: '250px', minWidth: '0px' } },
            { title: 'Balance', dataIndex: 'balance', style: { maxWidth: '190px', minWidth: '0px' } },
            { title: 'Percentage', dataIndex: 'percentage', style: { maxWidth: '10px', minWidth: '0px' } },
            {
              title: 'Txn Count',
              dataIndex: 'txnCount',
            },
          ]}
          dataSource={accounts.map((ac, index) => {
            return {
              ...ac,
              rank: (currentPage - 1) * 20 + index + 1,
              balance: (
                <>
                  {roundDownFixedPoint(weiToEther(ac.balance))} {ac.symbol}
                </>
              ),
              address: (
                <>
                  {ac.isContract && <Icon name="contractTable" />}
                  <Button text={ac.address} onClick={() => onClickAddress(ac.address)} />
                </>
              ),
              percentage: <>{ac.percentage ? roundDownFixedPoint(ac.percentage ?? 0) : ''} %</>,
              txnCount: commaSeparator(ac.txCount),
            }
          })}
          loading={isLoading}
        />
      </ContentsWrapper>
      <PaginationSingle
        totalPage={totalPage}
        current={currentPage}
        onClickLeft={onPrev}
        onClickRight={onNext}
        onClickFirstPage={onFirst}
        onClickLastPage={onLast}
      />
      <PageLoading loading={isLoading} />
    </MainContentDiv>
  )
}

export default TopAccount
